// src/pages/AnnouncementPage.js
//
// Announcement management page
// ============================
//
// PURPOSE
// -------
// Superuser-facing management UI for runtime announcements.
//
// The page supports the new central announcement model:
// - internal Navbar messages,
// - public shell banners such as /shop,
// - future route-specific internal or public messages.
//
// IMPORTANT CONTENT RULE
// ----------------------
// Announcement message text is user-managed business content. It is not stored
// in frontend i18n JSON files. Only labels around the management UI are
// translated by React i18next.

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './AnnouncementPage.css';
import {
    fetchAllAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    startAnnouncement,
    stopAnnouncement,
    deleteAnnouncement,
    getAnnouncementApiErrorMessage,
} from '../services/announcementService';

const EMPTY_FORM = {
    message_pl: '',
    message_en: '',
    message: '',
    audience: 'internal',
    surface: 'internal_navbar',
    target_type: 'all',
    target_value: '',
    severity: 'info',
    ticker_speed: 'normal',
    priority: 0,
    starts_at: '',
    ends_at: '',
    is_dismissible: false,
    link_url: '',
    link_label: '',
};

const AUDIENCE_OPTIONS = ['internal', 'public', 'both'];
const TARGET_TYPE_OPTIONS = ['all', 'shell', 'exact_path', 'path_prefix'];
const SEVERITY_OPTIONS = ['info', 'warning', 'error', 'success'];
const TICKER_SPEED_OPTIONS = [
    'very_slow',
    'slow',
    'normal',
    'slightly_fast',
    'fast',
    'very_fast',
    'maximum',
];

const SURFACE_OPTIONS = [
    'internal_navbar',
    'public_shell_banner',
    'internal_page_banner',
    'public_page_banner',
];

function toLocalDateTimeInput(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const pad = (n) => String(n).padStart(2, '0');
    return [
        date.getFullYear(),
        '-',
        pad(date.getMonth() + 1),
        '-',
        pad(date.getDate()),
        'T',
        pad(date.getHours()),
        ':',
        pad(date.getMinutes()),
    ].join('');
}

function normalizeApiDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
}

function announcementToForm(announcement) {
    return {
        message_pl: announcement.message_pl || announcement.message || '',
        message_en: announcement.message_en || announcement.message || '',
        message: announcement.message || '',
        audience: announcement.audience || 'internal',
        surface: announcement.surface || 'internal_navbar',
        target_type: announcement.target_type || 'all',
        target_value: announcement.target_value || '',
        severity: announcement.severity || 'info',
        ticker_speed: announcement.ticker_speed || 'normal',
        priority: Number(announcement.priority || 0),
        starts_at: toLocalDateTimeInput(announcement.starts_at),
        ends_at: toLocalDateTimeInput(announcement.ends_at),
        is_dismissible: Boolean(announcement.is_dismissible),
        link_url: announcement.link_url || '',
        link_label: announcement.link_label || '',
    };
}

function buildPayload(form) {
    return {
        message_pl: form.message_pl.trim() || null,
        message_en: form.message_en.trim() || null,
        message: form.message.trim() || form.message_pl.trim() || form.message_en.trim(),
        audience: form.audience,
        surface: form.surface,
        target_type: form.target_type,
        target_value: form.target_type === 'all' ? null : form.target_value.trim() || null,
        severity: form.severity,
        ticker_speed: form.ticker_speed,
        priority: Number(form.priority || 0),
        starts_at: normalizeApiDate(form.starts_at),
        ends_at: normalizeApiDate(form.ends_at),
        is_dismissible: Boolean(form.is_dismissible),
        link_url: form.link_url.trim() || null,
        link_label: form.link_label.trim() || null,
    };
}

function getEffectiveMessage(announcement) {
    return announcement.message_pl || announcement.message_en || announcement.message || '';
}

const AnnouncementPage = () => {
    const { t } = useTranslation(['settings', 'common']);

    const [announcements, setAnnouncements] = useState([]);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const isEditing = Boolean(editingId);

    useEffect(() => {
        loadAnnouncements();
   
    }, []);

    const sortedAnnouncements = useMemo(() => {
        return [...announcements].sort((a, b) => {
            const aActive = a.is_active ? 1 : 0;
            const bActive = b.is_active ? 1 : 0;
            if (aActive !== bActive) return bActive - aActive;

            const aPriority = Number(a.priority || 0);
            const bPriority = Number(b.priority || 0);
            if (aPriority !== bPriority) return bPriority - aPriority;

            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });
    }, [announcements]);

    const setField = (field, value) => {
        setForm((prev) => {
            const next = { ...prev, [field]: value };

            // Convenience defaults: when switching audience, move to the matching
            // main surface unless the user already selected a page-specific one.
            if (field === 'audience') {
                if (value === 'public' && prev.surface === 'internal_navbar') {
                    next.surface = 'public_shell_banner';
                }
                if (value === 'internal' && prev.surface === 'public_shell_banner') {
                    next.surface = 'internal_navbar';
                }
            }

            return next;
        });
    };

    const loadAnnouncements = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await fetchAllAnnouncements();
            setAnnouncements(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error('[AnnouncementPage] Error fetching announcements:', err);
            setError(getAnnouncementApiErrorMessage(err, t('settings:announcements.messages.loadFailed')));
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        const payload = buildPayload(form);
        if (!payload.message) return;

        setSaving(true);

        try {
            if (isEditing) {
                await updateAnnouncement(editingId, payload);
            } else {
                await createAnnouncement(payload);
            }

            resetForm();
            await loadAnnouncements();
        } catch (err) {
            console.error('[AnnouncementPage] Error saving announcement:', err);

            const fallbackMessage = isEditing
                ? t('settings:announcements.messages.updateFailed')
                : t('settings:announcements.messages.createFailed');

            setError(getAnnouncementApiErrorMessage(err, fallbackMessage));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (announcement) => {
        setEditingId(announcement.id);
        setForm(announcementToForm(announcement));
        setError('');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleToggleAnnouncement = async (id, isActive) => {
        try {
            if (isActive) {
                await stopAnnouncement(id);
            } else {
                await startAnnouncement(id);
            }
            await loadAnnouncements();
        } catch (err) {
            console.error(`[AnnouncementPage] Failed to toggle announcement ${id}:`, err);
            setError(getAnnouncementApiErrorMessage(err, t('settings:announcements.messages.toggleFailed')));
        }
    };

    const handleDeleteAnnouncement = async (id) => {
        if (!window.confirm(t('settings:announcements.confirmDelete'))) return;

        try {
            await deleteAnnouncement(id);
            if (editingId === id) resetForm();
            await loadAnnouncements();
        } catch (err) {
            console.error(`[AnnouncementPage] Failed to delete announcement ${id}:`, err);
            setError(getAnnouncementApiErrorMessage(err, t('settings:announcements.messages.deleteFailed')));
        }
    };

    return (
        <div className="announcement-page">
            <div className="announcement-page__header">
                <div>
                    <h1>{t('settings:announcements.title')}</h1>
                    <p>{t('settings:announcements.subtitle')}</p>
                </div>
            </div>

            {error ? <p className="announcement-page__error">{error}</p> : null}

            <form className="announcement-form" onSubmit={handleSubmit}>
                <div className="announcement-form__header">
                    <h2>
                        {isEditing
                            ? t('settings:announcements.form.editTitle')
                            : t('settings:announcements.form.createTitle')}
                    </h2>
                    {isEditing ? (
                        <button type="button" className="announcement-button announcement-button--ghost" onClick={resetForm}>
                            {t('common:actions.cancel')}
                        </button>
                    ) : null}
                </div>

                <div className="announcement-form__grid">
                    <label className="announcement-field announcement-field--wide">
                        <span>{t('settings:announcements.fields.messagePl')}</span>
                        <textarea
                            rows={2}
                            value={form.message_pl}
                            onChange={(e) => setField('message_pl', e.target.value)}
                            placeholder={t('settings:announcements.placeholders.messagePl')}
                        />
                    </label>

                    <label className="announcement-field announcement-field--wide">
                        <span>{t('settings:announcements.fields.messageEn')}</span>
                        <textarea
                            rows={2}
                            value={form.message_en}
                            onChange={(e) => setField('message_en', e.target.value)}
                            placeholder={t('settings:announcements.placeholders.messageEn')}
                        />
                    </label>

                    <label className="announcement-field announcement-field--wide">
                        <span>{t('settings:announcements.fields.messageFallback')}</span>
                        <input
                            type="text"
                            value={form.message}
                            onChange={(e) => setField('message', e.target.value)}
                            placeholder={t('settings:announcements.placeholders.messageFallback')}
                        />
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.audience')}</span>
                        <select value={form.audience} onChange={(e) => setField('audience', e.target.value)}>
                            {AUDIENCE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {t(`settings:announcements.options.audience.${option}`)}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.surface')}</span>
                        <select value={form.surface} onChange={(e) => setField('surface', e.target.value)}>
                            {SURFACE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {t(`settings:announcements.options.surface.${option}`)}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.targetType')}</span>
                        <select value={form.target_type} onChange={(e) => setField('target_type', e.target.value)}>
                            {TARGET_TYPE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {t(`settings:announcements.options.targetType.${option}`)}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.targetValue')}</span>
                        <input
                            type="text"
                            value={form.target_value}
                            disabled={form.target_type === 'all'}
                            onChange={(e) => setField('target_value', e.target.value)}
                            placeholder={form.target_type === 'path_prefix' ? '/shop' : '/shop/thank-you'}
                        />
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.severity')}</span>
                        <select value={form.severity} onChange={(e) => setField('severity', e.target.value)}>
                            {SEVERITY_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {t(`settings:announcements.options.severity.${option}`)}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.tickerSpeed')}</span>
                        <select value={form.ticker_speed} onChange={(e) => setField('ticker_speed', e.target.value)}>
                            {TICKER_SPEED_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {t(`settings:announcements.options.tickerSpeed.${option}`)}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.priority')}</span>
                        <input
                            type="number"
                            value={form.priority}
                            onChange={(e) => setField('priority', e.target.value)}
                        />
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.startsAt')}</span>
                        <input
                            type="datetime-local"
                            value={form.starts_at}
                            onChange={(e) => setField('starts_at', e.target.value)}
                        />
                    </label>

                    <label className="announcement-field">
                        <span>{t('settings:announcements.fields.endsAt')}</span>
                        <input
                            type="datetime-local"
                            value={form.ends_at}
                            onChange={(e) => setField('ends_at', e.target.value)}
                        />
                    </label>

                    <label className="announcement-field announcement-field--wide">
                        <span>{t('settings:announcements.fields.linkUrl')}</span>
                        <input
                            type="text"
                            value={form.link_url}
                            onChange={(e) => setField('link_url', e.target.value)}
                            placeholder="https://... or /app/..."
                        />
                    </label>

                    <label className="announcement-field announcement-field--wide">
                        <span>{t('settings:announcements.fields.linkLabel')}</span>
                        <input
                            type="text"
                            value={form.link_label}
                            onChange={(e) => setField('link_label', e.target.value)}
                        />
                    </label>

                    <label className="announcement-checkbox announcement-field--wide">
                        <input
                            type="checkbox"
                            checked={form.is_dismissible}
                            onChange={(e) => setField('is_dismissible', e.target.checked)}
                        />
                        <span>{t('settings:announcements.fields.dismissible')}</span>
                    </label>
                </div>

                <div className="announcement-form__actions">
                    <button type="submit" className="announcement-button announcement-button--primary" disabled={saving}>
                        {saving
                            ? t('settings:announcements.actions.saving')
                            : isEditing
                                ? t('settings:announcements.actions.update')
                                : t('settings:announcements.actions.create')}
                    </button>
                </div>
            </form>

            <section className="announcement-list" aria-busy={loading}>
                <div className="announcement-list__header">
                    <h2>{t('settings:announcements.listTitle')}</h2>
                    <button type="button" className="announcement-button announcement-button--ghost" onClick={loadAnnouncements}>
                        {t('common:actions.refresh', { defaultValue: 'Refresh' })}
                    </button>
                </div>

                {loading ? (
                    <p className="announcement-page__muted">{t('settings:announcements.loading')}</p>
                ) : sortedAnnouncements.length > 0 ? (
                    sortedAnnouncements.map((announcement) => (
                        <article
                            key={announcement.id}
                            className={`announcement-item ${announcement.is_active ? 'announcement-item--active' : ''}`}
                        >
                            <div className="announcement-item__main">
                                <div className="announcement-item__topline">
                                    <span className={`announcement-pill announcement-pill--${announcement.severity || 'info'}`}>
                                        {announcement.severity || 'info'}
                                    </span>
                                    <span className="announcement-pill">
                                        {t(`settings:announcements.options.tickerSpeed.${announcement.ticker_speed || 'normal'}`)}
                                    </span>
                                    <span className="announcement-pill">{announcement.audience || 'internal'}</span>
                                    <span className="announcement-pill">{announcement.surface || 'internal_navbar'}</span>
                                    <span className="announcement-pill">{announcement.target_type || 'all'}</span>
                                </div>

                                <p className="announcement-item__message">{getEffectiveMessage(announcement)}</p>

                                <p className="announcement-item__meta">
                                    {announcement.target_value || t('settings:announcements.targetAll')}
                                    {' · '}
                                    {t('settings:announcements.priorityLabel', { priority: announcement.priority || 0 })}
                                </p>
                            </div>

                            <div className="announcement-item__actions">
                                <button
                                    type="button"
                                    onClick={() => handleEdit(announcement)}
                                    className="announcement-button announcement-button--ghost"
                                >
                                    {t('common:actions.edit', { defaultValue: 'Edit' })}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleToggleAnnouncement(announcement.id, announcement.is_active)}
                                    className={announcement.is_active ? 'announcement-button announcement-button--danger' : 'announcement-button announcement-button--success'}
                                >
                                    {announcement.is_active
                                        ? t('settings:announcements.actions.stop')
                                        : t('settings:announcements.actions.start')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                                    className="announcement-button announcement-button--dangerOutline"
                                >
                                    {t('common:actions.delete')}
                                </button>
                            </div>
                        </article>
                    ))
                ) : (
                    <p className="announcement-page__muted">{t('settings:announcements.empty')}</p>
                )}
            </section>
        </div>
    );
};

export default AnnouncementPage;
